import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Film, Users, BarChart3, Settings, Plus, Trash2, Edit, Eye,
  TrendingUp, Layers, Upload, X, Save, Search, Play, Tv,
  UserX, UserCheck, KeyRound, ChevronLeft, ChevronRight,
  Tag, Globe, Star, CreditCard, Check, Ticket, Calendar, Percent,
  Volume2, Joystick, ChevronUp, ChevronDown, RefreshCw, Sparkles, SlidersHorizontal, AlertTriangle, Scissors, GripVertical
} from 'lucide-react';
import { TrimEditor, fmtTrim } from './TrimEditor';
import { AiTrailerButton, AiTrailerBulkButton } from './AiTrailer';
import { ConfirmModal } from './TrashTab';
import FilterTimelineEditor from '../FilterTimelineEditor';
import { LanguageStats } from '../analytics/LanguageStats';
import {
  API, CATEGORIES_LIST, ALL_COUNTRIES, MEDIA_SPECS, LANGUAGES, TYPE_LABELS, TYPE_LABELS_PLURAL, uploadFileToS3, FilterBadge,
  formatFileSize, TimecodeInput, CountryPicker, FilterSelect, LoadingSpinner as SharedLoadingSpinner, validateMediaFile
} from './shared';
import { EpisodePreview, EpisodePreviewModal } from './EpisodePreview';

export function CategoriesTab({ theme }) {
  const light = theme === 'light';
  const [cats, setCats] = useState([]);
  const [newName, setNewName] = useState('');
  const [newId, setNewId] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [expandedCat, setExpandedCat] = useState(null);
  const [catContents, setCatContents] = useState([]);
  const [loadingContents, setLoadingContents] = useState(false);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const dragContentItem = useRef(null);
  const dragContentOverItem = useRef(null);
  const dragContentData = useRef(null);
  const dropCatRef = useRef(null);
  const [dropCat, setDropCat] = useState(null);
  const [moveToast, setMoveToast] = useState(null);

  const reload = async () => {
    const { data } = await axios.get(`${API}/admin/categories`, { withCredentials: true });
    setCats(data.sort((a, b) => (a.order ?? 99) - (b.order ?? 99)));
  };
  useEffect(() => { reload(); }, []);

  const add = async () => {
    if (!newId || !newName) return;
    await axios.post(`${API}/admin/categories`, { id: newId, name: newName, order: cats.length }, { withCredentials: true });
    setNewId(''); setNewName('');
    await reload();
  };

  const toggle = async (cat) => {
    await axios.put(`${API}/admin/categories/${cat.id}`, { visible: !cat.visible }, { withCredentials: true });
    setCats(prev => prev.map(c => c.id === cat.id ? { ...c, visible: !c.visible } : c));
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    await axios.delete(`${API}/admin/categories/${id}`, { withCredentials: true });
    setCats(prev => prev.filter(c => c.id !== id));
    if (expandedCat === id) setExpandedCat(null);
  };

  const saveName = async (cat) => {
    if (!editName.trim()) return;
    await axios.put(`${API}/admin/categories/${cat.id}`, { name: editName }, { withCredentials: true });
    setCats(prev => prev.map(c => c.id === cat.id ? { ...c, name: editName } : c));
    setEditingId(null);
  };

  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOverItem.current === null || dragItem.current === dragOverItem.current) {
      dragItem.current = null; dragOverItem.current = null; return;
    }
    const items = [...cats];
    const draggedItem = items.splice(dragItem.current, 1)[0];
    items.splice(dragOverItem.current, 0, draggedItem);
    dragItem.current = null; dragOverItem.current = null;
    setCats(items);
    setSaving(true);
    await axios.put(`${API}/admin/categories-order`, { order: items.map(c => c.id) }, { withCredentials: true });
    setSaving(false);
  };

  // Load contents for a category
  const toggleExpand = async (catId) => {
    if (expandedCat === catId) { setExpandedCat(null); return; }
    setExpandedCat(catId);
    setLoadingContents(true);
    try {
      const { data } = await axios.get(`${API}/contents`, { params: { category: catId, include_drafts: true }, withCredentials: true });
      const sorted = data.sort((a, b) => {
        const oa = a.display_order ?? 999999;
        const ob = b.display_order ?? 999999;
        if (oa !== ob) return oa - ob;
        return (b.created_at_ts || 0) - (a.created_at_ts || 0);
      });
      setCatContents(sorted);
    } catch { setCatContents([]); }
    finally { setLoadingContents(false); }
  };

  const handleContentDragEnd = async () => {
    // Dropped on another category row -> move content
    const dragged = dragContentData.current;
    const targetCat = dropCatRef.current;
    dragContentData.current = null; dropCatRef.current = null; setDropCat(null);
    if (dragged && targetCat && targetCat !== dragged.fromCat) {
      dragContentItem.current = null; dragContentOverItem.current = null;
      setSaving(true);
      try {
        await axios.put(`${API}/admin/contents/${dragged.id}/move-category`, { from_cat: dragged.fromCat, to_cat: targetCat }, { withCredentials: true });
        setCatContents(prev => prev.filter(c => c.id !== dragged.id));
        const catName = cats.find(c => c.id === targetCat)?.name || targetCat;
        setMoveToast(`« ${dragged.title} » déplacé vers ${catName}`);
        setTimeout(() => setMoveToast(null), 3500);
      } catch (err) {
        alert('Déplacement échoué : ' + (err.response?.data?.detail || err.message));
      } finally { setSaving(false); }
      return;
    }
    if (dragContentItem.current === null || dragContentOverItem.current === null || dragContentItem.current === dragContentOverItem.current) {
      dragContentItem.current = null; dragContentOverItem.current = null; return;
    }
    const items = [...catContents];
    const draggedItem = items.splice(dragContentItem.current, 1)[0];
    items.splice(dragContentOverItem.current, 0, draggedItem);
    dragContentItem.current = null; dragContentOverItem.current = null;
    setCatContents(items);
    setSaving(true);
    await axios.put(`${API}/admin/contents-order`, { category: expandedCat, content_ids: items.map(c => c.id) }, { withCredentials: true });
    setSaving(false);
  };

  const removeFromCategory = async (item) => {
    if (!window.confirm(`Retirer « ${item.title} » de cette catégorie ? Il n'apparaîtra plus dans cette rangée côté utilisateur.`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/contents/${item.id}/category/${expandedCat}`, { withCredentials: true });
      setCatContents(prev => prev.filter(c => c.id !== item.id));
    } catch (err) {
      alert('Retrait échoué : ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  const inputCls = light ? 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400' : 'bg-white/5 border border-white/8 text-white';
  const cardCls = light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className={`text-2xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Catégories</h1>
        {saving && <span className={`text-xs ${light ? 'text-gray-400' : 'text-white/30'}`}>Sauvegarde...</span>}
      </div>
      {moveToast && (
        <div className="mb-4 rounded-lg px-3 py-2 text-xs bg-green-500/10 text-green-500" data-testid="move-category-toast">{moveToast}</div>
      )}
      <div className={`rounded-xl p-5 border mb-5 ${cardCls}`}>
        <h3 className={`text-sm font-semibold mb-3 ${light ? 'text-gray-800' : 'text-white'}`}>Ajouter une catégorie</h3>
        <div className="flex gap-2">
          <input value={newId} onChange={e => setNewId(e.target.value)} placeholder="ID (ex: action)" className={`rounded-lg px-3 py-2 text-xs flex-1 focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nom affiché" className={`rounded-lg px-3 py-2 text-xs flex-1 focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
          <button onClick={add} className="px-4 py-2 bg-[#E9641A] text-white text-xs font-semibold rounded-lg"><Plus size={14} /></button>
        </div>
      </div>
      <p className={`text-[10px] mb-3 ${light ? 'text-gray-400' : 'text-white/30'}`}>Glissez-déposez pour réorganiser. Cliquez sur une catégorie pour gérer ses contenus : réorganisez-les, retirez-les (croix) ou glissez-les sur une autre catégorie pour les déplacer.</p>
      <div className="space-y-1.5">
        {cats.map((cat, idx) => (
          <div key={cat.id}>
            <div
              draggable
              onDragStart={() => { dragItem.current = idx; }}
              onDragEnter={() => {
                if (dragContentData.current) {
                  dropCatRef.current = cat.id;
                  setDropCat(cat.id);
                  return;
                }
                dragOverItem.current = idx;
              }}
              onDragOver={(e) => e.preventDefault()}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-grab active:cursor-grabbing transition-all ${
                dropCat === cat.id && dragContentData.current
                  ? (light ? 'bg-green-50 border-green-400 ring-2 ring-green-400/40' : 'bg-green-500/10 border-green-400/50 ring-2 ring-green-400/30')
                  : expandedCat === cat.id
                  ? (light ? 'bg-orange-50 border-[#E9641A]/30 shadow-md' : 'bg-[#E9641A]/5 border-[#E9641A]/20')
                  : (light ? 'bg-white border-gray-200 shadow-sm hover:shadow-md' : 'bg-[#1a0e2e] border-white/5 hover:border-white/15')
              }`} data-testid={`cat-row-${cat.id}`}>
              <span className={`text-sm font-bold w-6 text-center ${light ? 'text-gray-300' : 'text-white/20'}`}>{idx + 1}</span>
              <div className={`flex flex-col gap-0.5 ${light ? 'text-gray-300' : 'text-white/20'}`}>
                <div className="w-4 h-0.5 bg-current rounded" /><div className="w-4 h-0.5 bg-current rounded" /><div className="w-4 h-0.5 bg-current rounded" />
              </div>
              <Tag size={14} className="text-[#E9641A] flex-none" />
              {editingId === cat.id ? (
                <input value={editName} onChange={e => setEditName(e.target.value)} onBlur={() => saveName(cat)} onKeyDown={e => e.key === 'Enter' && saveName(cat)}
                  className={`text-sm flex-1 rounded px-2 py-1 focus:outline-none focus:border-[#E9641A] ${inputCls}`} autoFocus />
              ) : (
                <span className={`text-sm flex-1 cursor-pointer hover:underline ${light ? 'text-gray-700' : 'text-white'}`}
                  onDoubleClick={() => { setEditingId(cat.id); setEditName(cat.name); }}
                  onClick={() => toggleExpand(cat.id)}>{cat.name}</span>
              )}
              <span className={`text-[10px] font-mono ${light ? 'text-gray-400' : 'text-white/30'}`}>{cat.id}</span>
              <button onClick={() => toggle(cat)} className={`px-2 py-1 text-[10px] rounded ${cat.visible ? 'bg-green-500/15 text-green-400' : light ? 'bg-gray-100 text-gray-400' : 'bg-white/5 text-white/30'}`}>{cat.visible ? 'Visible' : 'Masqué'}</button>
              <button onClick={() => del(cat.id)} className={`p-1.5 rounded ${light ? 'text-gray-300 hover:text-red-500' : 'text-white/25 hover:text-red-400'}`}><Trash2 size={13} /></button>
              <ChevronDown size={14} className={`transition-transform ${expandedCat === cat.id ? 'rotate-180' : ''} ${light ? 'text-gray-400' : 'text-white/30'}`} />
            </div>

            {/* Expanded contents panel */}
            {expandedCat === cat.id && (
              <div className={`mt-1 ml-6 p-4 rounded-lg border ${light ? 'bg-gray-50 border-gray-100' : 'bg-white/[0.02] border-white/5'}`} data-testid={`cat-contents-${cat.id}`}>
                {loadingContents ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="w-5 h-5 border-2 border-[#E9641A]/30 border-t-[#E9641A] rounded-full animate-spin" />
                  </div>
                ) : catContents.length === 0 ? (
                  <p className={`text-xs text-center py-4 ${light ? 'text-gray-400' : 'text-white/30'}`}>Aucun contenu dans cette catégorie.</p>
                ) : (
                  <>
                    <p className={`text-[10px] mb-3 ${light ? 'text-gray-400' : 'text-white/30'}`}>Glissez les vignettes pour réorganiser (gauche = premier affiché). Glissez une vignette sur une autre catégorie pour l'y déplacer.</p>
                    <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
                      {catContents.map((item, cIdx) => (
                        <div key={item.id}
                          draggable
                          onDragStart={(e) => { e.stopPropagation(); dragContentItem.current = cIdx; dragContentData.current = { id: item.id, title: item.title, fromCat: cat.id }; }}
                          onDragEnter={(e) => { e.stopPropagation(); dragContentOverItem.current = cIdx; dropCatRef.current = null; setDropCat(null); }}
                          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                          onDragEnd={(e) => { e.stopPropagation(); handleContentDragEnd(); }}
                          className={`group/thumb relative flex-none w-36 cursor-grab active:cursor-grabbing rounded-lg overflow-hidden border transition-all hover:scale-105 ${
                            light ? 'border-gray-200 bg-white' : 'border-white/10 bg-[#1a0e2e]'
                          }`}
                          data-testid={`content-order-${item.id}`}>
                          <div className="relative aspect-video">
                            <img src={item.thumbnail_url || item.banner_url} alt={item.title} className="w-full h-full object-cover" />
                            <span className="absolute top-1 left-1 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">{cIdx + 1}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); removeFromCategory(item); }}
                              title="Retirer de cette catégorie"
                              className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-opacity"
                              data-testid={`remove-from-cat-${item.id}`}>
                              <X size={11} />
                            </button>
                          </div>
                          <p className={`text-[10px] font-medium px-2 py-1.5 truncate ${light ? 'text-gray-700' : 'text-white/70'}`}>{item.title}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

